import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { Button, Input, Spinner, Badge } from "../../components/ui/UI";
import { sendEmailOTP, verifyEmailOTP } from "../../services/otp";
import { signUp, generatePassword } from "../../services/auth";
import { addPurchase, getUser } from "../../services/userData";
import { formatPrice } from "../../data/courses";
import styles from "./Checkout.module.css";

// ── Load Razorpay script ──────────────────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// ── Step 1: Contact details ───────────────────────────────────────────────────
function StepContact({ onNext }) {
  const { buyerInfo, setBuyerInfo } = useApp();
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const validate = () => {
    const e = {};
    if (!buyerInfo.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Enter a valid email";
    if (!buyerInfo.phone.match(/^\d{10}$/))                    e.phone = "Enter a 10-digit phone number";
    if (!buyerInfo.name?.trim())                                e.name  = "Enter your name";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    setApiError("");
    try {
      await sendEmailOTP({ email: buyerInfo.email, name: buyerInfo.name });
      onNext();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.stepBox}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>✉️</div>
        <h2>Enter Your Details</h2>
        <p>We'll email you a verification code, then send your course PDF after payment.</p>
      </div>

      <div className={styles.form}>
        <div>
          <Input
            label="Full Name"
            placeholder="As it should appear on your invoice"
            value={buyerInfo.name || ""}
            onChange={(e) => {
              setBuyerInfo((p) => ({ ...p, name: e.target.value }));
              setErrors((p) => ({ ...p, name: "" }));
            }}
          />
          {errors.name && <p className={styles.error}>⚠ {errors.name}</p>}
        </div>

        <div>
          <Input
            label="Email Address"
            type="email"
            placeholder="you@example.com"
            value={buyerInfo.email}
            onChange={(e) => {
              setBuyerInfo((p) => ({ ...p, email: e.target.value }));
              setErrors((p) => ({ ...p, email: "" }));
            }}
          />
          {errors.email && <p className={styles.error}>⚠ {errors.email}</p>}
        </div>

        <div>
          <Input
            label="Phone Number"
            type="tel"
            placeholder="10-digit mobile number"
            value={buyerInfo.phone}
            maxLength={10}
            onChange={(e) => {
              setBuyerInfo((p) => ({ ...p, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }));
              setErrors((p) => ({ ...p, phone: "" }));
            }}
          />
          {errors.phone && <p className={styles.error}>⚠ {errors.phone}</p>}
        </div>

        {apiError && (
          <div className={styles.apiError}>
            <span>⚠</span> {apiError}
          </div>
        )}

        <p className={styles.hint}>
          📩 A 6-digit OTP will be sent to your email.<br />
          Phone is for our contact records only.
        </p>

        <Button variant="primary" size="lg" fullWidth onClick={handleSubmit} disabled={loading}>
          {loading ? <><Spinner /> Sending OTP…</> : "Send OTP →"}
        </Button>
      </div>
    </div>
  );
}

// ── Step 2: OTP Verification ──────────────────────────────────────────────────
function StepOTP({ onNext, onBack }) {
  const { buyerInfo } = useApp();
  const [otp, setOtp]             = useState("");
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent]       = useState(false);

  const handleVerify = async () => {
    if (otp.length !== 6) { setError("Please enter the 6-digit OTP"); return; }
    setLoading(true);
    setError("");
    try {
      await verifyEmailOTP({ email: buyerInfo.email, otp });
      onNext();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError("");
    try {
      await sendEmailOTP({ email: buyerInfo.email, name: buyerInfo.name });
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={styles.stepBox}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>🔐</div>
        <h2>Verify your email</h2>
        <p>
          A 6-digit code was sent to <strong>{buyerInfo.email}</strong>
          <br />
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            Don't see it? Check your spam folder.
          </span>
        </p>
      </div>

      <div className={styles.form}>
        <div className={styles.otpInputWrap}>
          <input
            className={styles.otpInput}
            type="tel"
            inputMode="numeric"
            placeholder="——————"
            maxLength={6}
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
              setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleVerify()}
            autoFocus
          />
        </div>

        {error && <p className={styles.error} style={{ textAlign: "center" }}>⚠ {error}</p>}

        <p className={styles.hint} style={{ textAlign: "center" }}>
          {resent ? (
            <span style={{ color: "var(--green)" }}>✅ OTP resent!</span>
          ) : (
            <>
              Didn't receive it?{" "}
              <button className={styles.resendBtn} onClick={handleResend} disabled={resending}>
                {resending ? "Resending…" : "Resend OTP"}
              </button>
            </>
          )}
        </p>

        <Button variant="primary" size="lg" fullWidth onClick={handleVerify} disabled={loading || otp.length !== 6}>
          {loading ? <><Spinner /> Verifying…</> : "Verify & Continue →"}
        </Button>

        <Button variant="ghost" size="md" fullWidth onClick={onBack}>← Change email</Button>
      </div>
    </div>
  );
}

// ── Step 3: Razorpay Payment ──────────────────────────────────────────────────
function StepPayment({ onSuccess }) {
  const { cart, cartTotal, setOrderId, buyerInfo } = useApp();
  const { refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const handlePayment = async () => {
    setLoading(true); setError("");

    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setError("Failed to load payment gateway. Please check your connection.");
      setLoading(false); return;
    }

    try {
      // Step 1: Create order server-side
      const orderRes = await fetch("/.netlify/functions/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          courseIds: cart.map((c) => c.id),
          email:     buyerInfo.email,
          name:      buyerInfo.name,
        }),
      });

      if (!orderRes.ok) {
        const e = await orderRes.json();
        throw new Error(e.error || "Failed to create order");
      }

      const { orderId: razorpayOrderId, amount, currency, keyId } = await orderRes.json();

      // Step 2: Open Razorpay popup
      await new Promise((resolve, reject) => {
        const options = {
          key:         keyId,
          amount,
          currency,
          name:        "Shortly Life",
          description: cart.map((c) => c.title).join(", "),
          order_id:    razorpayOrderId,
          prefill: {
            name:    buyerInfo.name,
            email:   buyerInfo.email,
            contact: `+91${buyerInfo.phone}`,
          },
          theme: { color: "#7c3aed" },
          modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
          handler: async (response) => {
            try {
              // Step 3: Verify payment server-side
              const verifyRes = await fetch("/.netlify/functions/verify-payment", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id:   response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature:  response.razorpay_signature,
                }),
              });
              if (!verifyRes.ok) {
                const e = await verifyRes.json();
                throw new Error(e.error || "Payment verification failed");
              }
              resolve(response);
            } catch (err) { reject(err); }
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (r) => reject(new Error(r.error?.description || "Payment failed")));
        rzp.open();
      });

      // Step 4: Create account + save purchases
      const newOrderId = "CC-" + Date.now().toString().slice(-8);
      let password  = null;
      let isNewUser = false;

      let existing = null;
      try { existing = await getUser({ email: buyerInfo.email }); } catch {}

      if (!existing) {
        password  = generatePassword();
        isNewUser = true;
        try {
          await signUp({ email: buyerInfo.email, password });
        } catch (err) {
          if (err.message?.includes("already-in-use") || err.code === "auth/email-already-in-use") {
            isNewUser = false; password = null;
          } else {
            console.warn("Account creation:", err.message);
          }
        }
      }

      // Step 5: Save purchases to Firestore
      for (const course of cart) {
        try {
          await addPurchase({
            phone:    buyerInfo.phone,
            email:    buyerInfo.email,
            name:     buyerInfo.name || "",
            course,
            orderId:  newOrderId,
            pdfPassword: "",
          });
        } catch (err) { console.warn("Firestore save failed:", err.message); }
      }

      // Step 6: Log to Google Sheets
      try {
        if (process.env.REACT_APP_SHEETS_WEBHOOK) {
          await fetch(process.env.REACT_APP_SHEETS_WEBHOOK, {
            method: "POST", mode: "no-cors",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              orderId: newOrderId,
              name:    buyerInfo.name  || "",
              phone:   buyerInfo.phone || "",
              email:   buyerInfo.email || "",
              courses: cart.map((c) => c.title).join(", "),
              amount:  cartTotal,
              payment: "Razorpay — Verified",
            }),
          });
        }
      } catch {}

      // Step 7: Send welcome email with login credentials (new users only)
      if (isNewUser && password) {
        try {
          await fetch("/.netlify/functions/send-welcome", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email:       buyerInfo.email,
              name:        buyerInfo.name,
              password,
              orderId:     newOrderId,
              courseTitle: cart.map((c) => c.title).join(", "),
              loginUrl:    window.location.origin + "/login",
            }),
          });
        } catch {}
      }

      // Step 8: Deliver watermarked password-protected PDF via Brevo
      try {
        const coursesToDeliver = cart.filter(
          (c) => c.driveFileId && c.driveFileId !== "YOUR_GOOGLE_DRIVE_FILE_ID"
        );
        if (coursesToDeliver.length > 0) {
          await fetch("/.netlify/functions/deliver-course", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              orderId:    newOrderId,
              buyerName:  buyerInfo.name  || "",
              buyerEmail: buyerInfo.email,
              courses: coursesToDeliver.map((c) => ({
                title:       c.title,
                driveFileId: c.driveFileId,
              })),
            }),
          });
        }
      } catch (err) {
        console.warn("PDF delivery failed:", err.message);
      }

      // Step 9: Refresh auth state
      try { await refreshUserData(); } catch {}

      setOrderId(newOrderId);
      onSuccess({ isNewUser, password });

    } catch (err) {
      if (err.message !== "Payment cancelled") setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.stepBox}>
      <div className={styles.stepHeader}>
        <div className={styles.stepIcon}>💳</div>
        <h2>Complete Payment</h2>
        <p>Secure payment via Razorpay. Supports UPI, cards, netbanking and wallets.</p>
      </div>

      <div className={styles.paymentBox}>
        <div className={styles.paymentMethods}>
          {["📱 UPI", "💳 Cards", "🏦 Netbanking", "👛 Wallets"].map((m) => (
            <div key={m} className={styles.paymentMethod}>
              <p>{m}</p>
            </div>
          ))}
        </div>

        <div className={styles.amountRow}>
          <span>Total payable</span>
          <strong className={styles.amountBig}>{formatPrice(cartTotal)}</strong>
        </div>

        <div className={styles.securityRow}>
          <span>🔒 256-bit SSL encrypted</span>
          <span>✅ Razorpay secured</span>
        </div>
      </div>

      {error && (
        <p className={styles.error} style={{ textAlign: "center", marginBottom: 12 }}>⚠ {error}</p>
      )}

      <Button variant="primary" size="lg" fullWidth onClick={handlePayment} disabled={loading}>
        {loading ? <><Spinner /> Processing…</> : `Pay ${formatPrice(cartTotal)} →`}
      </Button>

      <p className={styles.hint} style={{ textAlign: "center", marginTop: 12 }}>
        By paying you agree to our{" "}
        <a href="/terms" style={{ color: "var(--accent3)" }}>Terms of Service</a>
      </p>
    </div>
  );
}

// ── Step 4: Success ───────────────────────────────────────────────────────────
function StepSuccess({ isNewUser, password }) {
  const { cart, orderId, buyerInfo, clearCart } = useApp();
  const navigate = useNavigate();

  return (
    <div className={styles.successBox}>
      <div className={styles.successAnim}>🎉</div>
      <h2 className={styles.successTitle}>You're all set!</h2>
      <p className={styles.successSub}>
        {isNewUser
          ? <>Your account has been created. Login details emailed to <strong>{buyerInfo.email}</strong>.</>
          : <>Your courses are now available. Login to <strong>{buyerInfo.email}</strong> to access them.</>}
      </p>

      {isNewUser && password && (
        <div style={{
          width: "100%",
          background: "rgba(124,58,237,0.08)",
          border: "1px solid rgba(124,58,237,0.25)",
          borderRadius: 12,
          padding: 16,
          textAlign: "center",
        }}>
          <p style={{ fontSize: 12, color: "var(--text2)", marginBottom: 4 }}>Your temporary password</p>
          <p style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 700, color: "var(--accent3)", letterSpacing: 2 }}>
            {password}
          </p>
          <p style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>
            Also sent to your email. You can change it later from your dashboard.
          </p>
        </div>
      )}

      <div className={styles.orderBox}>
        <div className={styles.orderRow}>
          <span>Order ID</span>
          <strong>{orderId}</strong>
        </div>
        {cart.map((c) => (
          <div key={c.id} className={styles.orderCourse}>
            <span>📄 {c.title}</span>
            <span>{formatPrice(c.price)}</span>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 13, color: "var(--text2)", textAlign: "center", marginBottom: 16 }}>
        📧 Your course PDF has been sent to <strong>{buyerInfo.email}</strong>
      </p>

      <Button variant="primary" size="lg" onClick={() => { clearCart(); navigate("/my-courses"); }}>
        Go to My Courses →
      </Button>
    </div>
  );
}

// ── Order Summary ─────────────────────────────────────────────────────────────
function OrderSummary() {
  const { cart, cartTotal } = useApp();
  return (
    <div className={styles.summary}>
      <h3 className={styles.summaryTitle}>Order Summary</h3>
      <div className={styles.summaryList}>
        {cart.map((c) => (
          <div key={c.id} className={styles.summaryItem}>
            <div>
              <p className={styles.summaryName}>{c.title}</p>
              <p className={styles.summaryPages}>📄 {c.pages} pages</p>
            </div>
            <strong>{formatPrice(c.price)}</strong>
          </div>
        ))}
      </div>
      <div className={styles.summaryTotal}>
        <span>Total</span>
        <strong>{formatPrice(cartTotal)}</strong>
      </div>
      <div className={styles.summaryBadges}>
        <Badge color="green">⚡ Instant delivery</Badge>
        <Badge color="accent">🔒 Secure payment</Badge>
      </div>
    </div>
  );
}

const STEPS = ["Contact", "Verify Email", "Payment", "Done"];

function StepsBar({ current }) {
  return (
    <div className={styles.stepsBar}>
      {STEPS.map((s, i) => (
        <div key={i} className={styles.stepItem}>
          <div className={[
            styles.stepDot,
            i < current   ? styles.stepDone   : "",
            i === current ? styles.stepActive  : "",
          ].join(" ")}>
            {i < current ? "✓" : i + 1}
          </div>
          <span className={[styles.stepLabel, i === current ? styles.stepLabelActive : ""].join(" ")}>
            {s}
          </span>
          {i < STEPS.length - 1 && (
            <div className={[styles.stepLine, i < current ? styles.stepLineDone : ""].join(" ")} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function CheckoutPage() {
  const { cart } = useApp();
  const navigate  = useNavigate();
  const [step, setStep] = useState(0);
  const [successData, setSuccessData] = useState({ isNewUser: false, password: null });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  if (cart.length === 0 && step !== 3) {
    return (
      <div className={styles.emptyCart}>
        <span>🛒</span>
        <h2>Your cart is empty</h2>
        <p>Add some courses before checking out.</p>
        <Button variant="primary" onClick={() => navigate("/category/all")}>Browse Courses</Button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.inner}>
          <div className={styles.left}>
            <StepsBar current={step} />
            {step === 0 && <StepContact onNext={() => setStep(1)} />}
            {step === 1 && <StepOTP     onNext={() => setStep(2)} onBack={() => setStep(0)} />}
            {step === 2 && <StepPayment onSuccess={(data) => { setSuccessData(data); setStep(3); }} />}
            {step === 3 && <StepSuccess {...successData} />}
          </div>
          {step < 3 && (
            <div className={styles.right}>
              <OrderSummary />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}